import groupBy from 'lodash/groupBy';
import pick from 'lodash/pick';
import flatMap from 'lodash/flatMap';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import pickBy from 'lodash/pickBy';
import {
  DEFAULT_REVIEW_OF_SYSTEM_VALUE,
  getDefaultIf,
  getDefaultReviewOfSystemRegex,
  isEmptyString,
  notEmptyString,
  setDefaultValueIf,
} from '@populateui/utils';
import dayjs from 'utils/dayjs';
import {
  Patient,
  PatientForm,
} from 'pages/Dashboard/types/patient';
import {
  normalizeInsurance,
  normalizePatientName,
  normalizePhoneNumber,
  validatePhone,
} from 'pages/Dashboard/utils/helper';
import {
  formatPatient,
} from 'pages/Dashboard/pages/Charts/helper';
import {
  MedicationBaseDTO,
  PatientDTO,
  PatientVisitDTO,
  PhoneNumberDTO,
  ReviewOfSystemDTO,
  ReviewOfSystemElementDTO,
  SocialHistoryDTO,
  VisitNoteDTO,
} from 'dtos';
import {
  formatDateOnly,
} from 'utils/date';
import {
  PatientVisit,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/VisitsAndNotes';
import orderBy from 'lodash/orderBy';
import {
  getColorByName,
} from 'utils/colors';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import {
  getAge,
} from 'utils/misc';
import {
  addressRequiredFieldsNotEmpty,
  validateAddressInfo,
} from 'utils/react-hook-form';

type ComplexHeight = {
  feet?: number;
  inches?: number;
};

const demographicFields: string[] = [
  'firstName',
  'middleName',
  'lastName',
  'dateOfBirth',
  'genderDescription',
  'occupation',
  'height',
  'weight',
  'email',
  'shoeSize',
];

export const defaultDiscriminators = [
  'Constitutional',
  'Musculoskeletal',
  'Neurological',
  'Cardiovascular',
  'Dermatological',
  'HematologyOncology',
  'Genitourinary',
  'ENT',
  'Respiratory',
  'Gastrointestinal',
];

export const defaultSocialHistoryInfo = {
  tobaccoUsedInLast12Months: null,
  alcoholConsumedInLast12Months: null,
  haveYouEverPutInAnAdvanceCarePlan: null,
  hadOneOrMoreMinorOrFallsInLast12Months: null,
};

export const parseHeightInFeet = (heightInInches: number): ComplexHeight => {
  const feet = Math.floor(heightInInches / 12);
  const inches = Math.floor(heightInInches % 12);
  return { feet, inches };
};

export const convertHeightToInches = ({
  feet = 0,
  inches = 0,
}: ComplexHeight): number | undefined => {
  const height = feet * 12 + inches;
  return height === 0 ? undefined : height;
};

export function normalizeReviewOfSystem(reviewOfSystem: ReviewOfSystemDTO) {
  const groupedByDiscriminator = groupBy(
    reviewOfSystem?.reviewOfSystemElements ?? [],
    'discriminator',
  );
  const groupedElements: Record<string, ReviewOfSystemElementDTO[]> = {};

  defaultDiscriminators.forEach((discriminator) => {
    groupedElements[discriminator] = (discriminator in groupedByDiscriminator
      ? groupedByDiscriminator[discriminator]
      : [{ discriminator, title: DEFAULT_REVIEW_OF_SYSTEM_VALUE, reviewOfSystemElementId: -1 }]
    );
  });

  return groupedElements;
}

export const transformPatient = (patient: PatientDTO): PatientForm => {
  const socialHistory = merge({
    ...defaultSocialHistoryInfo,
  }, patient.socialHistory);
  const {
    firstSeenOn,
    lastSeenOn,
  } = patient.primaryCarePhysician ?? {};

  const { contactName, phoneNumber, email } = patient?.otherContact ?? {};
  return ({
    ...formatPatient(patient) as Patient,
    ...setDefaultValueIf(pick(patient, demographicFields), '', isNil),
    address: {
      ...patient.address,
      ...setDefaultValueIf(patient.address ?? {}, '', isNil),
    },
    insurances: (patient?.insurances ?? [])?.map((insurance) => ({
      ...insurance,
      mspType: { code: insurance.mspType },
      insuranceOwnerDOB: dayjs(insurance.insuranceOwnerDOB),
      effectiveFrom: dayjs(insurance.effectiveFrom),
      effectiveTill: isNil(insurance.effectiveTill) ? null : dayjs(insurance.effectiveTill),
    })),
    socialHistory,
    ...parseHeightInFeet(patient?.height ?? 0),
    groupedReviewOfSystemElements: normalizeReviewOfSystem(patient.reviewOfSystem!),
    visitNotes: patient.visitNotes?.sort((visitNote1: VisitNoteDTO, visitNote2: VisitNoteDTO) => (
      dayjs(visitNote2.visitDateTime).unix() - dayjs(visitNote1.visitDateTime).unix()
    )),
    stickies: [],
    pinnedStickyCount: patient?.pinnedStickyCount ?? 0,
    acceptsCommunication: patient?.acceptsCommunication ?? false,
    isDeceased: patient?.isDeceased ?? false,
    isInactive: patient?.isInactive ?? false,
    primaryCarePhysician: {
      ...(patient.primaryCarePhysician ?? {}),
      providerEntry: patient.primaryCarePhysician?.providerEntry ?? null,
      firstSeenOn: getDefaultIf(firstSeenOn, '', isNil),
      lastSeenOn: getDefaultIf(lastSeenOn, '', isNil),
    },
    otherContact: {
      ...patient.otherContact,
      contactName: getDefaultIf(contactName, '', isNil),
      email: getDefaultIf(email, '', isNil),
      phoneNumber: getDefaultIf(phoneNumber, '', isNil),
    },
  });
};

const serializeReviewOfSystems = (
  groupedReviewOfSystemElements: Record<string, ReviewOfSystemElementDTO[]>,
): ReviewOfSystemElementDTO[] => (
  flatMap(values(groupedReviewOfSystemElements).map((item: ReviewOfSystemElementDTO[]) => {
    const { discriminator } = item[0] ?? {};
    const hasValidValue = item.some((element) => (
      !getDefaultReviewOfSystemRegex(true).test(element.title!.trim())
    ));
    const elements = item.filter((element) => (
      !getDefaultReviewOfSystemRegex(hasValidValue).test(element.title!.trim())
    ));
    const defaultValues = [{
      reviewOfSystemElementId: -1,
      discriminator,
      title: 'Patient Denies',
    }];
    return elements.length > 0 ? elements : defaultValues;
  }))
);

export const serializePatient = (patient: Partial<PatientForm>): Partial<Patient> => {
  const {
    address = {},
    feet,
    inches,
    patientId,
    medications,
    medicalHistories,
    drugAllergies,
    nonDrugAllergies,
    familyHistories,
    socialHistory,
    surgicalHistories,
    reviewOfSystem,
    groupedReviewOfSystemElements,
    insurances = [],
  } = patient;

  const patientWithNormalizedNames = {
    ...patient,
    ...('firstName' in patient ? { firstName: normalizePatientName(patient.firstName) } : {}),
    ...('middleName' in patient ? { middleName: normalizePatientName(patient.middleName) } : {}),
    ...('lastName' in patient ? { lastName: normalizePatientName(patient.lastName) } : {}),
  };

  const filterKeys = (value: unknown, key: string): boolean => (
    key in patient
    || (key === 'reviewOfSystem' && !isNil(groupedReviewOfSystemElements))
    || (key === 'height' && !isNil(inches) && !isNil(feet))
  );

  return pickBy({
    patientId,
    ...patientWithNormalizedNames,
    ...setDefaultValueIf(
      patientWithNormalizedNames,
      null,
      isEmptyString,
    ),
    insurances: insurances.map(normalizeInsurance),
    address: {
      ...patient.address,
      ...setDefaultValueIf(address, null, isEmptyString),
      country: 'USA',
    },
    height: convertHeightToInches({ feet, inches }),
    socialHistory,
    medications: medications?.map((item: MedicationBaseDTO) => ({
      ...item,
      ...setDefaultValueIf(
        item,
        null,
        isEmptyString,
      ),
    })),
    medicalHistories,
    drugAllergies,
    nonDrugAllergies,
    familyHistories,
    surgicalHistories,
    reviewOfSystem: {
      ...reviewOfSystem,
      reviewOfSystemElements: serializeReviewOfSystems(groupedReviewOfSystemElements!),
    },
  }, filterKeys);
};

export function normalizeSocialHistory(socialHistory: SocialHistoryDTO, dateOfBirth?: string) {
  const underSixtyFiveSocialHistory = {
    tobaccoUsedInLast12Months: socialHistory.tobaccoUsedInLast12Months,
    alcoholConsumedInLast12Months: socialHistory.alcoholConsumedInLast12Months,
  };
  const { value: age } = getAge(dateOfBirth, 'year');
  return age < 65 ? underSixtyFiveSocialHistory : socialHistory;
}

export function hasSocialHistoryAnswers(socialHistory?: SocialHistoryDTO | null): boolean {
  return Object.values(socialHistory ?? {})
    .some((value) => notEmptyString(value ?? ''));
}

function normalizePCPDate(date: string) {
  return notEmptyString(date) && dayjs(date).isValid()
    ? formatDateOnly(date ?? '')
    : null;
}

function normalizeOtherContactAddress(
  address: NonNullable<PatientForm['otherContact']>['address'],
) {
  return (
    !isNil(address)
    && validateAddressInfo(address)
    && addressRequiredFieldsNotEmpty(address)
  )
    ? {
      ...address,
      ...setDefaultValueIf(address, null, isEmptyString),
    }
    : undefined;
}

export function normalizeDemographicPayload({
  socialHistory,
  phoneNumber,
  dateOfBirth,
  otherPhoneNumber,
  occupationStartDate,
  primaryCarePhysician,
  hasNoPrimaryCarePhysician,
  otherContact,
  ...data
}: Partial<PatientForm>) {
  const normalizedOtherPhoneNumber = normalizePhoneNumber(
    otherPhoneNumber?.phoneNumberWithAreaCode ?? '',
  );

  return {
    ...data,
    ...(!isNil(dateOfBirth)
      ? { dateOfBirth: formatDateOnly(dateOfBirth ?? '') }
      : {}
    ),
    ...(!isNil(socialHistory) && hasSocialHistoryAnswers(socialHistory)
      ? { socialHistory: normalizeSocialHistory(socialHistory, dateOfBirth ?? '') }
      : {}
    ),
    ...(!isNil(occupationStartDate)
      ? { occupationStartDate: formatDateOnly(occupationStartDate ?? '') }
      : {}
    ),
    ...(!isNil(phoneNumber)
      ? {
        phoneNumber: {
          ...phoneNumber as PhoneNumberDTO,
          phoneNumberWithAreaCode: normalizePhoneNumber(phoneNumber.phoneNumberWithAreaCode ?? ''),
        },
      } : {}
    ),
    ...(!isNil(otherPhoneNumber)
      ? {
        otherPhoneNumber: notEmptyString(normalizedOtherPhoneNumber)
          ? {
            ...otherPhoneNumber,
            phoneNumberWithAreaCode: normalizedOtherPhoneNumber,
          }
          : undefined,
      } : {}
    ),
    ...(hasNoPrimaryCarePhysician ? { hasNoPrimaryCarePhysician: true } : {}),
    ...(!hasNoPrimaryCarePhysician && !isNil(primaryCarePhysician)
      ? {
        primaryCarePhysician: {
          providerEntry: primaryCarePhysician.providerEntry ?? null,
          firstSeenOn: normalizePCPDate(primaryCarePhysician.firstSeenOn ?? ''),
          lastSeenOn: normalizePCPDate(primaryCarePhysician.lastSeenOn ?? ''),
        },
      }
      : {}
    ),
    ...(hasNoPrimaryCarePhysician === false && isNil(primaryCarePhysician)
      ? { hasNoPrimaryCarePhysician: false }
      : {}
    ),
    ...(Object.values(omit(otherContact ?? {}, ['patientId', 'address', 'roles', 'isBillingContact', 'phoneNumber'])).every(isEmptyString) ? {} : {
      otherContact: {
        ...otherContact,
        address: normalizeOtherContactAddress(otherContact?.address),
        roles: getDefaultIf(otherContact?.roles, null, isEmpty),
        phoneNumber: validatePhone(otherContact?.phoneNumber)
          ? normalizePhoneNumber(otherContact?.phoneNumber ?? '') : null,
        contactName: getDefaultIf(otherContact?.contactName, null, isEmptyString),
        email: getDefaultIf(otherContact?.email, null, isEmptyString),
        isBillingContact: getDefaultIf(otherContact?.isBillingContact, false, isNil),
      },
    }),
  };
}

export function normalizePatientVisits(
  visits: PatientVisitDTO[],
  colorsHashMap: Record<string, string>,
): PatientVisit[] {
  return orderBy(
    visits?.map((item) => ({
      ...item,
      serviceFacilityColor: getColorByName((colorsHashMap[item.serviceFacility?.nickname ?? ''] ?? '!bg-emerald-200').replace('!bg-', '')),
    })),
    'visitDateTime',
    'desc',
  );
}
